import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BillingProvider, useBilling } from "./context/BillingContext";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Holdings from "./pages/Holdings";
import Dividends from "./pages/Dividends";
import CalendarPage from "./pages/CalendarPage";
import TaxReport from "./pages/TaxReport";
import Settings from "./pages/Settings";
import Ladder from "./pages/Ladder";
import Catalog from "./pages/Catalog";
import DatabaseBDC from "./pages/DatabaseBDC";
import DatabaseBDCDiscover from "./pages/DatabaseBDCDiscover";
import DatabaseGeneric from "./pages/DatabaseGeneric";
import CallRisk from "./pages/CallRisk";
import Pricing from "./pages/Pricing";
import BillingSuccess from "./pages/BillingSuccess";
import AdminPromos from "./pages/AdminPromos";
import AdminUsers from "./pages/AdminUsers";
import BrokerCallback from "./pages/BrokerCallback";
import Layout from "./components/Layout";
import UpgradeModal from "./components/UpgradeModal";
import { Toaster } from "sonner";
import { attachBillingHandler } from "./lib/api";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-champagne flex items-center justify-center text-paars-dark font-mono text-sm tracking-widest uppercase animate-pulse">
        Initializing tracker...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  const { openUpgrade } = useBilling();

  // Wire 402 → open upgrade modal globally (idempotent)
  React.useEffect(() => {
    attachBillingHandler((detail) => {
      openUpgrade(detail?.code || "free_tier_limit_reached");
    });
  }, [openUpgrade]);

  // Handle session_id in URL fragment (race-condition safe)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
      <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/holdings" element={<Protected><Holdings /></Protected>} />
      <Route path="/database/bdc" element={<Protected><DatabaseBDC /></Protected>} />
      <Route path="/database/bdc/discover" element={<Protected><DatabaseBDCDiscover /></Protected>} />
      <Route path="/database/:type" element={<Protected><DatabaseGeneric /></Protected>} />
      <Route path="/call-risk" element={<Protected><CallRisk /></Protected>} />
      <Route path="/dividends" element={<Protected><Dividends /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/tax-report" element={<Protected><TaxReport /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/ladder" element={<Protected><Ladder /></Protected>} />
      <Route path="/catalog" element={<Protected><Catalog /></Protected>} />
      <Route path="/admin/promos" element={<Protected><AdminPromos /></Protected>} />
      <Route path="/admin/users" element={<Protected><AdminUsers /></Protected>} />
      <Route path="/broker/callback" element={<BrokerCallback />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BillingProvider>
          <BrowserRouter>
            <AppRouter />
            <UpgradeModal />
            <Toaster theme="light" position="top-right" toastOptions={{
              style: { background: "#ffffff", border: "1px solid #dbd6d2", borderRadius: 0, color: "#1e1e1c", fontFamily: "JetBrains Mono" },
            }} />
          </BrowserRouter>
        </BillingProvider>
      </AuthProvider>
    </div>
  );
}
